import React from 'react';
import { useEffect, useState } from 'react';
import { ContentfulDownloadSoftware, DownloadLink, OS } from '../../../interfaces';
import { Translate } from '../../layout';
import { Icon } from '../icon';
import { RichTextRenderer } from '../shared/rich-text-renderer';
import { getCurrentOS } from '../../../utils/get-current-os';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { withContentfulLivePreviewSys } from '../../../utils/with-contentful-live-preview-sys';
import { fetchSoftwareVersion, SoftwareValues } from '../../../api/software-version-api.service';

import './download-software.scss';

type Props = ContentfulDownloadSoftware & {
  downloadVersion?: string;
  previewVersion?: string;
};

const DownloadSoftware = (props: Props) => {
  const liveData = useContentfulLiveUpdates(withContentfulLivePreviewSys(props));
  const {
    productName,
    logo,
    releaseNotesLink,
    showPreviewVersionNumber,
    showDownloadVersionNumber,
    linksDownload,
    isMobileApp,
    contentful_id,
    additionalMessage
  } = liveData;

  const [version, setVersion] = React.useState<SoftwareValues | null>(null);

  React.useEffect(() => {
    if (!showPreviewVersionNumber && !showDownloadVersionNumber) return;

    (async () => {
      try {
        const values: SoftwareValues | null = await fetchSoftwareVersion(productName);

        setVersion(values);
      } catch (err) {
        console.error("Error fetching software versions in container:", err);
      }
    })();
    
  }, [productName]);

  const [selectedLink, setSelectedLink] = useState<DownloadLink | null>(null);

  const inspector = useContentfulInspectorMode(
    contentful_id ? { entryId: contentful_id } : undefined
  );

  const logoInspector = inspector({ fieldId: 'logo' });
  const releaseNotesInspector = inspector({ fieldId: 'releaseNotesLink' });
  const linksInspector = inspector({ fieldId: 'linksDownload' });
  const productInspector = inspector({ fieldId: 'productName' });
  const additionalMsgInspector = inspector({ fieldId: 'text', entryId: additionalMessage?.contentful_id });

  const additionalMsgText = additionalMessage?.text?.raw ?? additionalMessage?.text?.json;

  if (!productName) return null;

  const currentOS = getCurrentOS()?.toLowerCase() || '';
  const isApple = currentOS.includes('mac') || currentOS.includes('ios');
  const iosLink = linksDownload?.find((link) => 
    link.os.toLowerCase().includes('ios')
  )?.iosDownloadLink;

  const compareVersions = (previewNumberVersion, downloadNumberVersion) => {
    const versionPreviewParts = previewNumberVersion.split('.').map(Number);
    const versionDownloadParts = downloadNumberVersion.split('.').map(Number);

    const maxLength = Math.max(versionPreviewParts.length, versionDownloadParts.length);

    for (let i = 0; i < maxLength; i++) {
      const partPreview = versionPreviewParts[i] || 0;
      const partDownload = versionDownloadParts[i] || 0;

      if (partPreview > partDownload) return true;
      if (partPreview < partDownload) return false;
    }

    return false;
  };

  const releaseVersion = version?.release_version;
  const previewVersion = version?.preview_version;

  const isPreviewButtonVisible = releaseVersion && previewVersion && compareVersions(previewVersion, releaseVersion);

  useEffect(() => {
    const downloadLink = linksDownload?.find((link) => {
      const os = link.os.toLowerCase();
        if (isApple){
          return os.includes('mac'); 
        } 
        return os.includes('windows') || os.includes('linux') || os.includes('android'); 
    });
    setSelectedLink(downloadLink || null);
  }, [linksDownload, isApple]);

  
  return (
    <div className={`software-container-card software-card-${productName}`} {...productInspector}>
      {logo?.file?.url && (
        <div className="software-container-card__image" {...logoInspector}>
          <img
            className="image"
            src={logo.file.url}
            alt={logo.title || "software logo"}
          />
        </div>
      )}

      <div className="software-container-card__content">
        <hr className='solid' />
        {releaseNotesLink && (
          <a href={releaseNotesLink} target="_blank" rel="noreferrer" className="release-notes" {...releaseNotesInspector}>
            <Translate id="component.download-software.release-notes" />
          </a>
        )}
      </div>

      <div className="software-container-card__buttons" {...linksInspector}>
        {selectedLink?.previewLink && isPreviewButtonVisible && (
          <div className='btn--download'>
            <a
              className="btn btn--secondary"
              href={selectedLink.previewLink}
              target="_blank"
              rel="noreferrer">
              <Translate id="component.download-software.download-preview" />
            </a>
            
            { showPreviewVersionNumber && previewVersion && (
              <span className="version">
                <Translate id="component.download-software.version" /> 
                {showPreviewVersionNumber && previewVersion && ` ${previewVersion}`}
              </span>
              )}
          </div>
        )}

        {selectedLink?.downloadLink && (
          <div className='btn--download'>
            <a className="btn btn--primary"
              href={selectedLink.downloadLink}
              target="_blank"
              rel="noreferrer" >
            <Translate id="component.download-software.download" />
            </a>
            { showDownloadVersionNumber && releaseVersion &&(
              <span className="version">
                <Translate id="component.download-software.version" />
                {showDownloadVersionNumber && releaseVersion && ` ${releaseVersion}`}
              </span>
            )}
          </div>
        )}

        {isMobileApp && iosLink && (
          <div className='btn--app'>
            <a className="btn btn--primary" href={iosLink} target="_blank" rel="noreferrer">
              <Icon color='white' icon={"apple"} />
              <span>
                <Translate id="component.download-software.app-store" />
              </span>
            </a>
          </div>
        )} 
      </div>
        {additionalMsgText && (
          <div className="software-container-card__additional_info" {...additionalMsgInspector} >
            <RichTextRenderer raw={additionalMsgText} />
          </div>
        )}
    </div>
  );
};

export default DownloadSoftware;